import { motion } from "framer-motion";
import CountUp from "react-countup";
import { BiLogoTypescript } from "react-icons/bi";
import {
  FaBootstrap,
  FaCss3Alt,
  FaFire,
  FaGithub,
  FaLock,
  FaNodeJs,
  FaReact,
  FaServer,
  FaStripe,
} from "react-icons/fa";
import {
  SiAxios,
  SiBun,
  SiDaisyui,
  SiMongodb,
  SiMui,
  SiNextdotjs,
  SiReactquery,
} from "react-icons/si";
import { TbBrandMysql } from "react-icons/tb";
import { useInView } from "react-intersection-observer";
import image from "../../assets/about.png";
import SectionTitle from "../../components/SectionTitle";
import { fadeIn } from "../../variants";

const About = () => {
  const [ref, inView] = useInView({
    threshold: 0.5,
  });

  return (
    <section className="min-h-[80vh] mt-10 lg:mt-16" id="about" ref={ref}>
      <div className="container mx-auto">
        <div className="lg:grid grid-cols-2 justify-center items-center">
          {/* Image */}
          <motion.div
            variants={fadeIn("right", 0.3)}
            initial="hidden"
            whileInView={"show"}
            viewport={{ once: true, amount: 0.3 }}
          >
            <img
              className="w-11/12 md:w-10/12 lg:w-8/12 mb-6 pt-3 lg:pt-0 rounded-2xl mx-auto lg:mx-0"
              src={image}
              alt="Yasin Arafat potfolio feature"
            />
          </motion.div>
          {/* Contents */}
          <motion.div
            variants={fadeIn("left", 0.3)}
            initial="hidden"
            whileInView={"show"}
            viewport={{ once: true, amount: 0.7 }}
          >
            {/* Title */}
            <SectionTitle normalText={"About"} coloredText={"Me"} />
            {/* Subtitle */}
            <h3 className="mb-4 text-[20px] md:text-[25px] font-secondary font-semibold uppercase leading-[1]">
              Hi, I'm Here To Help Your Next Project!
            </h3>
            {/* Description */}
            <p className="md:w-10/12 lg:w-full text-gray-300 leading-relaxed mb-6">
              I specialize in crafting high-performance, accessible, and
              user-focused web applications using modern frontend technologies
              and scalable architectures.
            </p>

            <div>
              <h4 className="text-base uppercase tracking-wider text-gray-300 mb-2">
                Tech Stack
              </h4>

              <div className="flex flex-wrap gap-3">
                {[
                  {
                    name: "React",
                    icon: <FaReact />,
                    style: "bg-sky-500/10 text-sky-400",
                  },
                  {
                    name: "Next.js",
                    icon: <SiNextdotjs />,
                    style: "bg-gray-500/10 text-gray-300",
                  },
                  {
                    name: "TypeScript",
                    icon: <BiLogoTypescript />,
                    style: "bg-blue-500/10 text-blue-400",
                  },
                  {
                    name: "Tailwind CSS",
                    icon: <FaCss3Alt />,
                    style: "bg-cyan-500/10 text-cyan-400",
                  },
                  {
                    name: "GitHub",
                    icon: <FaGithub />,
                    style: "bg-gray-800/10 text-gray-300",
                  },
                  {
                    name: "DaisyUI",
                    icon: <SiDaisyui />,
                    style: "bg-pink-300/10 text-pink-500",
                  },
                  {
                    name: "Bootstrap",
                    icon: <FaBootstrap />,
                    style: "bg-purple-500/10 text-purple-400",
                  },
                  {
                    name: "Material UI",
                    icon: <SiMui />,
                    style: "bg-indigo-500/10 text-indigo-400",
                  },

                  {
                    name: "React Bootstrap",
                    icon: <FaBootstrap />,
                    style: "bg-violet-500/10 text-violet-400",
                  },
                  {
                    name: "Node.js",
                    icon: <FaNodeJs />,
                    style: "bg-green-500/10 text-green-400",
                  },
                  {
                    name: "Bun",
                    icon: <SiBun />,
                    style: "bg-pink-500/10 text-pink-400",
                  },
                  {
                    name: "Express.js",
                    icon: <FaServer />,
                    style: "bg-gray-600/10 text-gray-300",
                  },
                  {
                    name: "JWT Auth",
                    icon: <FaLock />,
                    style: "bg-yellow-500/10 text-yellow-400",
                  },

                  {
                    name: "MongoDB",
                    icon: <SiMongodb />,
                    style: "bg-emerald-500/10 text-emerald-400",
                  },
                  {
                    name: "MySQL",
                    icon: <TbBrandMysql />,
                    style: "bg-blue-600/10 text-blue-400",
                  },
                  {
                    name: "React Query",
                    icon: <SiReactquery />,
                    style: "bg-red-500/10 text-red-400",
                  },
                  {
                    name: "Axios",
                    icon: <SiAxios />,
                    style: "bg-red-400/10 text-red-300",
                  },

                  {
                    name: "Firebase",
                    icon: <FaFire />,
                    style: "bg-orange-500/10 text-orange-400",
                  },
                  {
                    name: "Stripe",
                    icon: <FaStripe />,
                    style: "bg-violet-500/10 text-violet-400",
                  },
                ].map((tech) => (
                  <span
                    key={tech.name}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border border-white/10 transition-all duration-300 ease-in-out
  hover:scale-105 hover:-translate-y-1
  hover:shadow-lg hover:shadow-black/20 ${tech.style}`}
                  >
                    <span className="text-lg">{tech.icon}</span>
                    {tech.name}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
        {/* Stats */}
        <div className="flex gap-2 md:gap-x-6 lg:gap-x-10 my-10 px-3 md:px-0 justify-center items-center">
          <div>
            <div className="text-xl md:text-[40px] font-tertiary text-gradient mb-2">
              {inView ? (
                <CountUp start={0} end={3} duration={3} suffix="+" />
              ) : (
                "3+"
              )}
            </div>
            <div className="font-primary text-xs md:text-sm tracking-[2px]">
              Years of Experience
            </div>
          </div>
          <div>
            <div className="text-xl md:text-[40px] font-tertiary text-gradient mb-2">
              {inView ? (
                <CountUp start={0} end={30} duration={3} suffix="+" />
              ) : (
                "30+"
              )}
            </div>
            <div className="font-primary text-xs md:text-sm tracking-[2px]">
              Projects Completed
            </div>
          </div>
          <div>
            <div className="text-xl md:text-[40px] font-tertiary text-gradient mb-2">
              {inView ? (
                <CountUp start={0} end={110} duration={3} suffix="+" />
              ) : (
                "110+"
              )}
            </div>
            <div className="font-primary text-xs md:text-sm tracking-[2px]">
              GitHub Repositories
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
